import type { ChangeEvent, JSX, MouseEvent } from 'react';
import { useCallback } from 'react';
import clsx from 'clsx';

import { getProjectStatusColor } from '~/utils/projectStatus';

import type { ProjectStatus } from '~/types';

interface ProjectStatusSelectProps {
  status: ProjectStatus | string;
  onStatusChange: (status: ProjectStatus) => void;
  /** optional: swallow row/card click when embedded in a clickable parent (ProjectCard) */
  onClick?: (e: MouseEvent<HTMLSelectElement>) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * Shared status `<select>` for project rows/cards — extracted from
 * `ProjectCard.tsx`'s inline select so `ProjectView`'s header can reuse the
 * same SCOPING/TASKING→TODO display mapping, TASKING-disabled state, and
 * `getProjectStatusColor` styling without duplicating the logic.
 */
export function ProjectStatusSelect(props: ProjectStatusSelectProps): JSX.Element {
  const {
    'aria-label': ariaLabel = 'Project status',
    className,
    onClick,
    onStatusChange,
    status,
  } = props;

  const displayValue = status === 'SCOPING' || status === 'TASKING' ? 'TODO' : status;
  const isDisabled = status === 'TASKING';

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      e.stopPropagation();
      onStatusChange(e.target.value as ProjectStatus);
    },
    [onStatusChange],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLSelectElement>) => {
      e.stopPropagation();
      onClick?.(e);
    },
    [onClick],
  );

  return (
    <select
      aria-label={ariaLabel}
      value={displayValue}
      onChange={handleChange}
      onClick={handleClick}
      disabled={isDisabled}
      className={clsx(
        getProjectStatusColor(status),
        'px-1.5 py-0.5 rounded text-xs font-medium focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
    >
      <option value="TODO">Todo</option>
      <option value="IN_PROGRESS">In Progress</option>
      <option value="DONE">Done</option>
    </select>
  );
}
